import tenantTheme from '@theme';
import tenantUtils from '@utils';
import { Typography } from 'antd';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTransferListingsMutation } from '../../../apis/listings';
import { bulkChangeOwnerEvent } from '../../../services/analyticsService';
import { Alert, Button, DrawerModal, Flex, Icon, Image, TextInput, TextWithIcon, notification } from '../../common';
import {
  AssigneeRadioGrid,
  coverImageInBox,
  getInitials,
  toSafeUserIdString,
} from '../change-owner/assignee-radio-grid';
import { ChangeOwnerListingSummary, changeOwnerSummaryCardStyle } from '../change-owner/listing-summary';
import { getListingOwner, getSelectedCountLabel } from './bulkSelection';

// Same sizing as the single-listing Change Listing Ownership modal.
const MODAL_WIDTH_DESKTOP = 900;
const MODAL_BODY_PAD_X = 16;

const sectionTitleStyle = { fontWeight: 700, marginBottom: 12, fontSize: 14 };

const avatarStyle = {
  width: 20,
  height: 20,
  borderRadius: '50%',
  overflow: 'hidden',
  flexShrink: 0,
  background: '#e8e8e8',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: 8,
  lineHeight: 1,
  color: tenantTheme['primary-text-color'],
};

const getUserName = (u) => tenantUtils.getLocalisedString(u, 'name') || u?.name || u?.name_l1 || '—';

const getListingIdValue = (listing) => {
  const platforms = listing?.platforms;
  return (
    platforms?.bayut?.platform_listing_id ||
    platforms?.dubizzle?.platform_listing_id ||
    platforms?.unified?.platform_listing_id ||
    listing?.id
  );
};

const matchesAgentSearch = (u, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const digits = q.replace(/\s/g, '');
  return (
    [u?.name, u?.name_l1, u?.email].some((v) => v && String(v).toLowerCase().includes(q)) ||
    [u?.mobile, u?.phone].some((v) => v && String(v).replace(/\s/g, '').includes(digits))
  );
};

/** Change Listing Ownership, multi-listing variant (My Listings bulk Change Owner). */
export const BulkChangeOwnerModal = ({ bulkSelection, onClose }) => {
  const { t } = useTranslation();
  const { Text } = Typography;
  const { isMobile } = useSelector((state) => state.app.AppConfig);
  const { user } = useSelector((state) => state.app.loginUser);
  const agencyUsers = useSelector((state) => state.app.userGroup?.list) || [];
  const [transferListings, { isLoading }] = useTransferListingsMutation();
  const [newOwnerId, setNewOwnerId] = useState(null);
  const [query, setQuery] = useState('');

  const { selectedIds = [], setSelectedIds, listings = [], tab } = bulkSelection || {};
  const selectedListings = useMemo(
    () => selectedIds.map((id) => listings.find((listing) => listing?.id === id)).filter(Boolean),
    [selectedIds, listings],
  );
  const count = selectedListings.length;

  const currentAssignees = useMemo(() => {
    const groups = new Map();
    selectedListings.forEach((listing) => {
      const owner = getListingOwner(listing);
      const key = toSafeUserIdString(owner?.id) ?? 'unknown';
      const fromGroup = agencyUsers.find((u) => toSafeUserIdString(u?.id) === key);
      const existing = groups.get(key);
      groups.set(key, {
        key,
        name: existing?.name || (owner ? getUserName(owner) : null) || (fromGroup ? getUserName(fromGroup) : '—'),
        profile_image: existing?.profile_image || owner?.profile_image || fromGroup?.profile_image,
        count: (existing?.count || 0) + 1,
      });
    });
    return [...groups.values()];
  }, [selectedListings, agencyUsers]);

  // Like the single-listing modal: when every selected listing has the same owner, that agent can't be picked.
  const sharedOwnerId =
    currentAssignees.length === 1 && currentAssignees[0].key !== 'unknown' ? currentAssignees[0].key : null;

  const filteredUsers = useMemo(() => agencyUsers.filter((u) => matchesAgentSearch(u, query)), [agencyUsers, query]);
  // A pick that became the shared owner (after removing listings with ×) no longer counts.
  const effectiveOwnerId = newOwnerId !== sharedOwnerId ? newOwnerId : null;
  const pickedUser = agencyUsers.find((u) => toSafeUserIdString(u?.id) === effectiveOwnerId);
  const pickedName = pickedUser ? getUserName(pickedUser) : '';

  const removeListing = (id) => setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));

  const onConfirm = async () => {
    const userId = Number(pickedUser?.id);
    if (!pickedUser || !Number.isFinite(userId)) {
      notification.error(t('Please select a valid user.'));
      return;
    }
    const response = await transferListings({ userId, listingIds: selectedListings.map((l) => Number(l.id)) });
    if (response?.error) {
      notification.error(response?.error);
      return;
    }
    notification.success(
      count === 1
        ? t('Owner changed for {{count}} listing — now {{agent}}', { count, agent: pickedName })
        : t('Owner changed for {{count}} listings — now {{agent}}', { count, agent: pickedName }),
    );
    bulkChangeOwnerEvent(user, { listingsCount: count, tab });
    setSelectedIds([]);
    onClose();
  };

  return (
    <DrawerModal
      title={t('Change Listing Ownership')}
      visible
      onCancel={onClose}
      footer={null}
      width={isMobile ? undefined : MODAL_WIDTH_DESKTOP}
      height={isMobile ? '88vh' : 'auto'}
      bodyStyle={
        isMobile
          ? { minHeight: 200, maxHeight: 'calc(88vh - 120px)', overflowY: 'auto', padding: 12 }
          : { maxHeight: 'min(78vh, 720px)', overflowY: 'auto', padding: `8px ${MODAL_BODY_PAD_X}px 16px` }
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.3fr) minmax(0, 1fr)',
            gap: 12,
            alignItems: 'stretch',
          }}
        >
          <div style={{ ...changeOwnerSummaryCardStyle, maxWidth: '100%' }}>
            <div style={sectionTitleStyle}>{getSelectedCountLabel(t, count)}</div>
            <div
              style={{
                maxHeight: 236,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                paddingInlineEnd: 4,
              }}
            >
              {selectedListings.map((listing, index) => {
                const listingIdValue = getListingIdValue(listing);
                return (
                  <div
                    key={listing.id}
                    style={index ? { borderTop: '1px solid #f0f0f0', paddingTop: 12 } : undefined}
                  >
                    <ChangeOwnerListingSummary
                      compact
                      listingRow={listing}
                      idLabel={t('Listing ID')}
                      idValue={listingIdValue}
                      extra={
                        <Icon
                          icon="IoMdClose"
                          size={16}
                          color={tenantTheme.gray500}
                          className="pointer"
                          aria-label={`${t('Remove')} ${listingIdValue}`}
                          onClick={() => removeListing(listing.id)}
                        />
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ ...changeOwnerSummaryCardStyle, maxWidth: '100%' }}>
            <div style={sectionTitleStyle}>{t('Current assignees')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {currentAssignees.map((owner) => (
                <Flex key={owner.key} align="flex-start" gap="12px" style={{ width: '100%' }}>
                  <div style={avatarStyle}>
                    {owner.profile_image ? (
                      <Image src={owner.profile_image} {...coverImageInBox} />
                    ) : (
                      getInitials(owner.name)
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <Text strong ellipsis style={{ fontSize: 14, lineHeight: '20px', margin: 0 }}>
                      {owner.name}
                    </Text>
                    <TextWithIcon
                      icon="FaList"
                      gap="8px"
                      iconProps={{ size: 12, color: tenantTheme.gray500 }}
                      value={
                        owner.count === 1
                          ? t('{{count}} listing', { count: owner.count })
                          : t('{{count}} listings', { count: owner.count })
                      }
                      textStyle={{ fontSize: 12, lineHeight: 1.3 }}
                    />
                  </div>
                </Flex>
              ))}
            </div>
          </div>
        </div>

        <div>
          <div style={sectionTitleStyle}>{t('Select the new assignee')}</div>
          <TextInput
            value={query}
            handleChange={(e) => setQuery(e.target.value)}
            placeholder={t('Search by name, email or phone')}
            prefixIcon="BiSearch"
            iconColor={tenantTheme.gray500}
            groupStyle={{ marginBottom: 12 }}
          />
          {filteredUsers.length ? (
            <AssigneeRadioGrid
              users={filteredUsers}
              value={effectiveOwnerId}
              onChange={setNewOwnerId}
              disabledUserId={sharedOwnerId}
            />
          ) : (
            <Text type="secondary">{t('No agents match that search.')}</Text>
          )}
        </div>

        {!!pickedUser && count > 0 && (
          <Alert
            type="warning"
            showIcon
            message={
              count === 1
                ? t('The owner of {{count}} listing will change to {{agent}}.', { count, agent: pickedName })
                : t('The owner of all {{count}} listings will change to {{agent}}.', { count, agent: pickedName })
            }
          />
        )}

        <Flex justify="flex-end" gap="8px" style={{ marginTop: 4, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
          <Button type="default" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button type="primary" loading={isLoading} disabled={!pickedUser || count === 0} onClick={onConfirm}>
            {t('Change Ownership ({{count}})', { count })}
          </Button>
        </Flex>
      </div>
    </DrawerModal>
  );
};
